/**
 * fMP4 Demuxer — Wraps mp4box.js for robust fMP4 demuxing.
 *
 * Extracts raw NAL units from fragmented MP4 segments (HEVC or H.264).
 * Uses mp4box.js for ISO BMFF parsing — supports all box types,
 * extended sizes, multi-track, and proper hvcC/avcC extraction.
 */

import { createFile as mp4boxCreateFile } from "mp4box";
import type { MP4Info, MP4Sample, MP4Track, ArrayBufferWithStart } from "mp4box";
import { log } from "./log.js";

export interface DemuxedSample {
  trackId: number;
  /** Raw NAL units (no start code, no length prefix) */
  nalUnits: Uint8Array[];
  /** Presentation timestamp (in timescale units) */
  pts: number;
  /** Decode timestamp */
  dts: number;
  /** Duration (in timescale units) */
  duration: number;
  /** Whether this is a keyframe (IDR/CRA) */
  isKeyframe: boolean;
}

export interface VideoTrackInfo {
  trackId: number;
  codec: string;
  timescale: number;
  width: number;
  height: number;
  nalLengthSize: number;
}

export interface AudioTrackInfo {
  trackId: number;
  codec: string;
  timescale: number;
  channelCount: number;
  sampleRate: number;
  sampleSize: number;
  /** AudioSpecificConfig — the DecoderSpecificInfo bytes, passed through verbatim into the output esds. */
  asc: Uint8Array;
}

/** One demuxed audio sample, passed through untouched (raw AAC frame). */
export interface AudioSample {
  data: Uint8Array;
  pts: number;
  dts: number;
  duration: number;
}

/**
 * fMP4 Demuxer backed by mp4box.js.
 *
 * @example
 * ```ts
 * const demuxer = new FMP4Demuxer();
 * await demuxer.parse(initSegmentBytes);
 * const samples = await demuxer.extractSamples(mediaSegmentBytes);
 * ```
 */
export class FMP4Demuxer {
  private _mp4box = mp4boxCreateFile();
  private _videoTrack: VideoTrackInfo | null = null;
  private _audioTrack: AudioTrackInfo | null = null;
  private _pendingSamples: DemuxedSample[] = [];
  private _pendingAudio: AudioSample[] = [];
  private _offset = 0;
  private _ready = false;
  private _readyPromise: Promise<void>;
  private _readyResolve!: () => void;

  constructor() {
    this._readyPromise = new Promise<void>((resolve) => {
      this._readyResolve = resolve;
    });

    this._mp4box.onReady = (info: MP4Info) => {
      this._onReady(info);
    };

    this._mp4box.onSamples = (trackId: number, _user: unknown, samples: MP4Sample[]) => {
      if (this._audioTrack && trackId === this._audioTrack.trackId) {
        // Audio: pass through the raw sample bytes untouched (no NAL parsing).
        for (const sample of samples) {
          const buf: ArrayBuffer | Uint8Array = sample.data;
          this._pendingAudio.push({
            data: ArrayBuffer.isView(buf) ? new Uint8Array(buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength)) : new Uint8Array(buf),
            pts: sample.cts,
            dts: sample.dts,
            duration: sample.duration,
          });
        }
        return;
      }
      for (const sample of samples) {
        const nalUnits = extractNalUnitsFromSample(sample.data, this._videoTrack?.nalLengthSize ?? 4);
        this._pendingSamples.push({
          trackId: sample.track_id,
          nalUnits,
          pts: sample.cts,
          dts: sample.dts,
          duration: sample.duration,
          isKeyframe: sample.is_rap || sample.is_sync,
        });
      }
    };

    this._mp4box.onError = (e: Error) => {
      log.error("[FMP4Demuxer] mp4box error:", e);
    };
  }

  /** Get the video track info (available after parse) */
  get videoTrack(): VideoTrackInfo | null {
    return this._videoTrack;
  }

  /** Get the audio track info, if the segment carries a muxed audio track. */
  get audioTrack(): AudioTrackInfo | null {
    return this._audioTrack;
  }

  /** Drain all pending audio samples extracted so far (raw AAC frames). */
  drainAudioSamples(): AudioSample[] {
    const samples = this._pendingAudio;
    this._pendingAudio = [];
    return samples;
  }

  /** Whether the init segment has been parsed */
  get isReady(): boolean {
    return this._ready;
  }

  /**
   * Feed data to the demuxer. Works for both init and media segments.
   * Call with the init segment first, then media segments.
   */
  feed(data: Uint8Array): void {
    const buf = data.buffer.slice(
      data.byteOffset,
      data.byteOffset + data.byteLength,
    ) as ArrayBufferWithStart;
    buf.fileStart = this._offset;
    this._offset += data.byteLength;
    this._mp4box.appendBuffer(buf);
  }

  /**
   * Wait for the init segment to be parsed (moov box found).
   */
  async waitReady(): Promise<void> {
    return this._readyPromise;
  }

  /**
   * Drain all pending samples extracted so far.
   */
  drainSamples(): DemuxedSample[] {
    const samples = this._pendingSamples;
    this._pendingSamples = [];
    return samples;
  }

  /**
   * Convenience: parse init segment and wait for ready.
   */
  async parseInit(data: Uint8Array): Promise<void> {
    this.feed(data);
    await this.waitReady();
  }

  /**
   * Convenience: feed a media segment and return extracted samples.
   */
  parseSegment(data: Uint8Array): DemuxedSample[] {
    this.feed(data);
    return this.drainSamples();
  }

  /** Flush any remaining data in the parser */
  flush(): void {
    this._mp4box.flush();
  }

  private _onReady(info: MP4Info): void {
    // Find the first video track
    const videoTrack = info.videoTracks[0] ?? info.tracks.find(
      (t: MP4Track) => t.type === "video",
    );

    if (videoTrack) {
      const isHevc = /^(hev1|hvc1)/i.test(videoTrack.codec);
      this._videoTrack = {
        trackId: videoTrack.id,
        codec: videoTrack.codec,
        timescale: videoTrack.timescale,
        width: videoTrack.width ?? videoTrack.video?.width ?? 0,
        height: videoTrack.height ?? videoTrack.video?.height ?? 0,
        nalLengthSize: isHevc ? 4 : 4, // mp4box.js handles both
      };

      // Request sample extraction for the video track
      this._mp4box.setExtractionOptions(videoTrack.id, undefined, { nbSamples: 100 });
    }

    // Audio track (present in muxed A/V segments) — extracted for pass-through.
    const audioTrack = info.audioTracks?.[0] ?? info.tracks.find(
      (t: MP4Track) => t.type === "audio",
    );
    if (audioTrack) {
      const asc = extractAudioSpecificConfig(this._mp4box, audioTrack.id);
      this._audioTrack = {
        trackId: audioTrack.id,
        codec: audioTrack.codec,
        timescale: audioTrack.timescale,
        channelCount: audioTrack.audio?.channel_count ?? 2,
        sampleRate: audioTrack.audio?.sample_rate ?? 48000,
        sampleSize: audioTrack.audio?.sample_size ?? 16,
        asc: asc ?? new Uint8Array(0),
      };
      // Same batching hint as video — mp4box emits per fed segment either
      // way, but a matching value avoids implying different semantics.
      this._mp4box.setExtractionOptions(audioTrack.id, undefined, { nbSamples: 100 });
    }

    this._ready = true;
    this._readyResolve();
    this._mp4box.start();
  }
}

/**
 * Pull the AudioSpecificConfig (DecoderSpecificInfo) bytes out of the audio
 * track's esds via mp4box's parsed box tree. Passed through verbatim into
 * the output esds so the re-muxed audio stays bit-identical to the source.
 */
function extractAudioSpecificConfig(mp4box: ReturnType<typeof mp4boxCreateFile>, trackId: number): Uint8Array | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const moov = (mp4box as any).moov;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const trak = moov?.traks?.find((t: any) => t.tkhd?.track_id === trackId);
    const entry = trak?.mdia?.minf?.stbl?.stsd?.entries?.[0];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const descs = entry?.esds?.esd?.descs ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const decoderConfig = descs.find((d: any) => d.tag === 0x04);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dsi = decoderConfig?.descs?.find((d: any) => d.tag === 0x05);
    if (dsi?.data) return dsi.data instanceof Uint8Array ? dsi.data.slice() : new Uint8Array(dsi.data);
  } catch {
    /* fall through */
  }
  return null;
}

/**
 * Extract NAL units from length-prefixed sample data.
 */
function extractNalUnitsFromSample(data: ArrayBuffer | Uint8Array, nalLengthSize: number): Uint8Array[] {
  // mp4box.js may return Uint8Array instead of ArrayBuffer
  const buf = data instanceof ArrayBuffer ? data : data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);
  const nalUnits: Uint8Array[] = [];
  let offset = 0;

  while (offset + nalLengthSize <= buf.byteLength) {
    let nalLength: number;
    switch (nalLengthSize) {
      case 1: nalLength = view.getUint8(offset); break;
      case 2: nalLength = view.getUint16(offset); break;
      case 4: nalLength = view.getUint32(offset); break;
      default: nalLength = view.getUint32(offset); break;
    }
    offset += nalLengthSize;

    if (offset + nalLength > buf.byteLength) break;
    nalUnits.push(bytes.slice(offset, offset + nalLength));
    offset += nalLength;
  }

  return nalUnits;
}
